import React, { useLayoutEffect, useCallback, useEffect, useRef } from 'react';
import { Editor, type Element, Transforms, Range } from 'slate';
import {
  useSlateStatic,
  useSelected,
  ReactEditor,
  useReadOnly,
} from 'slate-react';
import { useController } from '@aha-app/mvc';
import AhaEditorV2Controller from '../../controllers/AhaEditorV2Controller';
import styles from './Resizer.module.css';
import classnames from 'classnames';
import DragHandle, { type DragHandlePosition } from './DragHandle';
import PixelSize from './PixelSize';
import { debounce } from 'lodash';
import useEventHandler from '../useEventHandler';
import type {
  EmbeddedReportElement,
  EmbeddedWhiteboardElement,
  EmbedElement,
  ImageElement,
  VideoElement,
} from '../../types/aha-editor';

const MIN_SIZE = 20;

function clampSize(currentSize: number, maxSize: number | undefined) {
  return Math.min(
    isNaN(currentSize) ? 0 : currentSize,
    !maxSize || isNaN(maxSize) ? Infinity : maxSize
  );
}

interface ResizerProps {
  width?: number;
  height?: number;
  originalWidth?: number;
  originalHeight?: number;
  // The element / slate node to resize.
  element:
    | EmbedElement
    | ImageElement
    | VideoElement
    | EmbeddedWhiteboardElement
    | EmbeddedReportElement;
  // The element is being actively resized.
  onResize?: (width: number, height: number) => void;
  // The element was resized.
  onResized?: (width: number, height: number) => void;
  // Whether the width:height ratio should be kept when resizing.
  keepRatio: boolean;
  // Images and videos can use height: auto because the media dictates the aspect ratio. But for embedded content, we cannot use this.
  useAutoHeight?: boolean;
  // Whether to show an indication of the current dimensions while resizing.
  showPixelSize?: boolean;
  // Whether the element supports live resizing. TODO: should this be in the schema?
  liveResize?: boolean;
  // Which handles should be displayed on the resizer.
  handles?: DragHandlePosition[];
  children: React.ReactNode;
  // Whether the resizer must force to fill all the horizontal space
  fullWidth?: boolean;
}

// Wrap an element in a container that shows handles for resizing.
const Resizer: React.FC<ResizerProps> = ({
  width,
  height,
  originalWidth,
  originalHeight,
  element,
  onResize,
  onResized,
  keepRatio,
  useAutoHeight = keepRatio,
  showPixelSize,
  liveResize,
  handles,
  children,
  fullWidth,
}) => {
  const editor = useSlateStatic();
  const selected = useSelected();
  const readOnly = useReadOnly();
  const focused = true; // useFocused();
  const resizerRef = React.useRef<HTMLDivElement>(null);
  const Tag = Editor.isInline(editor, element as Element) ? 'span' : 'div';
  const [resizing, setResizing] = React.useState(false); // Local state for resizing of the element
  const { actionSetResizing, editorContainer } = useController(
    AhaEditorV2Controller
  );
  const [localWidth, setLocalWidth] = React.useState(width);
  const [localHeight, setLocalHeight] = React.useState(height);
  const [localMaxWidth, setLocalMaxWidth] = React.useState(0);

  const prevDimensionProps = useRef({
    width,
    height,
  });

  const { selection } = editor;
  const active =
    selected &&
    focused &&
    selection &&
    !readOnly &&
    Range.isCollapsed(selection);

  useEffect(() => {
    if (
      width !== prevDimensionProps.current.width ||
      height !== prevDimensionProps.current.height ||
      fullWidth
    ) {
      prevDimensionProps.current.width = width;
      prevDimensionProps.current.height = height;
      setDimensions(width ?? 0, height ?? 0, false);
    }
  }, [width, height, fullWidth]);

  const containerWidth = editorContainer?.clientWidth;
  const setDimensions = useCallback(
    (
      width: number,
      height: number,
      commit: boolean,
      maxWidth = localMaxWidth
    ) => {
      height = clampSize(height, Infinity);

      if (fullWidth && containerWidth) {
        width = containerWidth;
      } else {
        width = Math.max(Math.round(clampSize(width, maxWidth)), MIN_SIZE);
      }

      if (keepRatio) {
        if (originalHeight && originalWidth) {
          const ratio = originalHeight / originalWidth;
          height = width * ratio;
        }
      } else {
        height = Math.max(height, MIN_SIZE);
      }
      height = Math.round(height);

      if (isNaN(height)) {
        console.error('Height is not a number');
      }

      setLocalWidth(width);
      setLocalHeight(height);

      if (commit) {
        setResizing(false);
        actionSetResizing(false);
        if (onResized) onResized(width, height);

        const path = ReactEditor.findPath(editor, element);
        Transforms.setNodes(
          editor,
          {
            resizer_width: width,
            resizer_height: height,
            resizer_originalWidth: originalWidth,
            resizer_originalHeight: originalHeight,
          },
          {
            at: path,
          }
        );
      } else {
        if (liveResize) {
          if (onResize) onResize(width, height);
        }
      }
    },
    [
      editor,
      element,
      keepRatio,
      liveResize,
      localMaxWidth,
      onResize,
      onResized,
      originalWidth,
      originalHeight,
    ]
  );

  // Don't allow the element to overflow the parent container, so we measure
  // the size of the parent and constrain the size of the element to that.
  const calculateMaxWidth = useCallback(() => {
    if (!resizerRef.current) return;

    const oldWidth = resizerRef.current.style.width;
    resizerRef.current.style.width = '100%';
    const elementWidth = resizerRef.current.clientWidth;
    resizerRef.current.style.width = oldWidth;

    // If we couldn't compute the max width, don't set it.
    if (elementWidth === 0) {
      return;
    }

    setLocalMaxWidth(elementWidth);
    // Update the width based on the new max width.
    setDimensions(width!, height!, false, elementWidth);
  }, [width, height, setDimensions]);

  // Modifying the window size might affect the max width.
  const onWindowResize = debounce(calculateMaxWidth, 100);
  useEventHandler('resize', () => onWindowResize(), window);

  // Calculate the maximum width when we first load.
  useLayoutEffect(() => {
    calculateMaxWidth();
  }, []);

  const handleMoveStart = () => {
    calculateMaxWidth();
    setResizing(true);
    actionSetResizing(true);
  };

  const handleMoved = (
    complete: boolean,
    position: DragHandlePosition,
    x: number,
    y: number
  ) => {
    if (!resizerRef.current) {
      return;
    }

    const rect = resizerRef.current.getBoundingClientRect();
    let width = 0;
    let height = 0;

    switch (position) {
      case 'w':
        width = rect.width - (x - rect.left);
        height = rect.height;
        break;
      case 'e':
        width = x - rect.left;
        height = rect.height;
        break;
      case 's':
        width = rect.width;
        height = y - rect.top;
        break;
      case 'se':
        width = x - rect.left;
        height = y - rect.top;
        break;
    }

    setDimensions(width, height, complete);
  };

  const pixelSize =
    showPixelSize && resizing ? (
      <PixelSize width={localWidth ?? 0} height={localHeight ?? 0} />
    ) : null;

  const handleComponents = active
    ? (handles ?? ['e']).map((position: DragHandlePosition) => (
        <DragHandle
          key={position}
          position={position}
          onMoveStart={handleMoveStart}
          onMove={(position, x, y) => handleMoved(false, position, x, y)}
          onMoved={(position, x, y) => handleMoved(true, position, x, y)}
        />
      ))
    : null;

  return (
    <Tag
      className={classnames(styles.resizer, {
        [styles.resizing]: resizing,
      })}
      style={{
        width: localWidth,
        height: keepRatio && useAutoHeight ? 'auto' : localHeight,
      }}
      ref={resizerRef}
    >
      {children}
      {pixelSize}
      {handleComponents}
    </Tag>
  );
};

export default Resizer;
